import React, { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { matchPath, useLocation } from 'react-router-dom'
import RoutePath from '../../../pure-js/libs/RoutePath'
import { resolveClientFromHostname } from '../libs/DBApiHandler'
import { useMounted } from '../hooks/useMounted'

type ClientRouteHostValue = {
  /** URL :clientId takes precedence when the current route carries one; otherwise the hostname-resolved clientId. Undefined if neither resolves. */
  clientId: string | undefined
  /** Current window.location.hostname, port stripped — the literal subdomain or custom domain in use. */
  clientHost: string
  /** True only while a hostname-resolution Firestore lookup is in flight. */
  isLoading: boolean
  /** Set if hostname resolution throws. */
  error: Error | null
}

const defaultValue: ClientRouteHostValue = {
  clientId: undefined,
  clientHost: '',
  isLoading: false,
  error: null
}

const ClientRouteHostContext = createContext<ClientRouteHostValue>(defaultValue)

// Every route pattern that carries a :clientId segment, e.g. /activities/:clientId,
// /checkout/:clientId/:productType. Derived from RoutePath so new routes are picked
// up automatically instead of drifting from a hand-maintained list.
const CLIENT_ID_ROUTE_PATTERNS: string[] = Object.values(RoutePath).filter((path) => path.includes(':clientId'))

function getClientIdFromPathname(pathname: string): string | undefined {
  for (const pattern of CLIENT_ID_ROUTE_PATTERNS) {
    const match = matchPath(pattern, pathname)
    if (match?.params.clientId) {
      return match.params.clientId
    }
  }
  return undefined
}

function getCurrentClientHost(): string {
  return typeof window !== 'undefined' ? window.location.hostname.split(':')[0] : ''
}

export const ClientRouteHostProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation()
  const isMounted = useMounted()

  const clientHost = useMemo(getCurrentClientHost, [])
  const routeClientId = useMemo(() => getClientIdFromPathname(location.pathname), [location.pathname])

  const [state, setState] = useState<{ clientId: string | undefined; isLoading: boolean; error: Error | null }>(() => ({
    clientId: routeClientId,
    isLoading: !routeClientId,
    error: null
  }))

  useEffect(() => {
    if (routeClientId) {
      setState({ clientId: routeClientId, isLoading: false, error: null })
      return
    }

    setState((prev) => ({ ...prev, isLoading: true, error: null }))

    resolveClientFromHostname(clientHost, isMounted)
      .then((resolvedClientId) => {
        if (!isMounted()) return
        setState({ clientId: resolvedClientId ?? undefined, isLoading: false, error: null })
      })
      .catch((err) => {
        if (!isMounted()) return
        setState({
          clientId: undefined,
          isLoading: false,
          error: err instanceof Error ? err : new Error(String(err))
        })
      })
  }, [routeClientId, clientHost, isMounted])

  const value = useMemo<ClientRouteHostValue>(
    () => ({ clientId: state.clientId, clientHost, isLoading: state.isLoading, error: state.error }),
    [state, clientHost]
  )

  return <ClientRouteHostContext.Provider value={value}>{children}</ClientRouteHostContext.Provider>
}

export const useClientRouteHost = () => useContext(ClientRouteHostContext)
